import { Injectable } from '@angular/core';
import { Subject, Observable } from "rxjs";
import { PlayerClockInfoProvider, PlayerClockAdjustment } from './playerClockInfoProvider';
import { Logger } from './logger';
import { DeviceClockLeapDetector } from './deviceClockLeapDetector';

export interface IClock {
    now: Date
}

export class DefaultClock {
    public get now(): Date {
        return new Date();
    }
}

// Set this value to the start date of the server certificate
//
// If the device's clock value comes before this, then Laqorr should
// not do anything until somebody adjusts the time on the device
//
// (Sometimes the device fixes its time spontaneously)
//
export const minimumValidDateTime = new Date("2026-04-12 00:00Z");

function applyAdjustment(d: Date, playerClockAdjustment: PlayerClockAdjustment) {
    if (!playerClockAdjustment) {
        return d;
    }
    const millisecondsSinceEpochDeviceLocal = d.getTime();
    const timeZoneOffsetMinutes = d.getTimezoneOffset();
    const timezoneOffsetMilliseconds = timeZoneOffsetMinutes * 60 * 1000;
    const millisecondsSinceEpochUtc = millisecondsSinceEpochDeviceLocal + timezoneOffsetMilliseconds;
    const millisecondsWithNewAdjustment = millisecondsSinceEpochUtc + playerClockAdjustment.timeZoneOffset + playerClockAdjustment.accuracyAdjustment;
    const newDate = new Date(millisecondsWithNewAdjustment);
    return newDate;
}

function getTotalAdjusmentMilliseconds(clockAdjustment: PlayerClockAdjustment) {
    return clockAdjustment.accuracyAdjustment + clockAdjustment.timeZoneOffset;
}



function clearAccuracyAdjustment(clockAdjustment?: PlayerClockAdjustment) : PlayerClockAdjustment | undefined {
    if (!clockAdjustment) {
        return undefined;
    } else {
        return {
            accuracyAdjustment: 0,
            timeZoneOffset: clockAdjustment.timeZoneOffset
        };
    }
}


// An interval function that syncs with the clock
// so it calls 'next' as close to the second changeover
// as possible
export function onEverySecond(clock: LocalTimeZoneClock) : Observable<Date> {
    return new Observable<Date>(
        (subscriber) => {
            let timeoutHandle:number = undefined;
            let isSubscribed = true;
            const setNextTimer = () => {
                if(timeoutHandle) {
                    window.clearTimeout(timeoutHandle);
                }
                if(isSubscribed) {
                    const now = clock.now.getTime();
                    const next = (1000 - (now%1000)) || 1000;
                    timeoutHandle = window.setTimeout(
                        () => {
                            try {
                                subscriber.next(new Date(now+next));
                            } finally {
                                if(isSubscribed) {
                                    setNextTimer();
                                }
                            }
                        },
                        next
                    );
                }
            };
            setNextTimer();
            const clockLeapSubscription = clock.$timeLeap.subscribe({
                next: () => {
                    if(isSubscribed) {
                        subscriber.next(clock.now);
                    }
                    setNextTimer();
                }
            });
            return {
                unsubscribe: () => {
                    isSubscribed = false;
                    if(timeoutHandle) {
                        window.clearTimeout(timeoutHandle);
                    }
                    clockLeapSubscription.unsubscribe()
                }
            }

        }
    );
}


@Injectable({
    providedIn: "root"
})
export class LocalTimeZoneClock implements IClock {

    private clockAdjustment?: PlayerClockAdjustment;

    private readonly timeLeapSubject = new Subject<void>();

    private readonly deviceClockLeapDetector: DeviceClockLeapDetector;


    public constructor(readonly playerClockInfoProvider: PlayerClockInfoProvider, readonly logger: Logger) {
        this.deviceClockLeapDetector = new DeviceClockLeapDetector(logger);
        this.deviceClockLeapDetector.$clockLeap.subscribe({
            next: () => {
                this.clockAdjustment = clearAccuracyAdjustment(this.clockAdjustment);
                this.timeLeapSubject.next()
                this.playerClockInfoProvider.refresh()
            }
        });
        this.deviceClockLeapDetector.commenceDetection();
        playerClockInfoProvider.$playerClockAdjustment.subscribe({
            next: clockInfo => {
                const invokeTimeLeap = (this.clockAdjustment && (getTotalAdjusmentMilliseconds(this.clockAdjustment) != getTotalAdjusmentMilliseconds(clockInfo)));
                this.clockAdjustment = clockInfo;
                if (invokeTimeLeap) {
                    this.timeLeapSubject.next();
                }
            }
        });
    }

    public get $timeLeap() {
        return this.timeLeapSubject.asObservable();
    }

    public get now(): Date {
        try {
            const date = new Date();
            if (!this.clockAdjustment) {
                return date;
            }
            else {
                return applyAdjustment(date, this.clockAdjustment);
            }
        } catch {
            return new Date();
        }
    }
}